import {
  ChangeDetectionStrategy,
  Component,
  input,
  Input,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  type IIntegration,
  IntegrationType,
} from '@principle-theorem/integrations';
import { FeatureFlagsService } from '@principle-theorem/ng-feature-flags';
import { EmptyStateComponent } from '@principle-theorem/ng-principle-shared';
import {
  BasicDialogService,
  ConfirmDialogComponent,
  DialogPresets,
  IConfirmationDialogInput,
  NgCommonModule,
  SplitCamelPipe,
  TypedFormControl,
  confirmationDialogData,
  formControlChanges$,
} from '@principle-theorem/ng-shared';
import {
  type IOrganisation,
  SMS_INTEGRATION_COUNTRY_MAP,
  SmsIntegrationOption,
  VIA_API_SMS_INTEGRATION_OPTION,
} from '@principle-theorem/principle-core/interfaces';
import { VIA_API_SMS_FEATURE } from '@principle-theorem/principle-core/features';
import {
  type CollectionReference,
  Firestore,
  Region,
  type WithRef,
  snapshot,
  titlecase,
} from '@principle-theorem/shared';
import { type ITemporaryToken } from '@principle-theorem/temporary-tokens';
import { type Observable, ReplaySubject, combineLatest, of } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { ManagePodiumIntegrationComponent } from '../manage-podium-integration/manage-podium-integration.component';
import { ManageTnzIntegrationComponent } from '../manage-tnz-integration/manage-tnz-integration.component';
import { ManageTwilioIntegrationComponent } from '../manage-twilio-integration/manage-twilio-integration.component';
import { ManageViaApiIntegrationComponent } from '../manage-via-api-integration/manage-via-api-integration.component';

@Component({
  selector: 'pr-manage-sms-integration',
  templateUrl: './manage-sms-integration.component.html',
  styleUrls: ['./manage-sms-integration.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgCommonModule,
    MatCardModule,
    MatFormFieldModule,
    MatSelectModule,
    MatButtonModule,
    ManageTwilioIntegrationComponent,
    ManagePodiumIntegrationComponent,
    ManageTnzIntegrationComponent,
    ManageViaApiIntegrationComponent,
    EmptyStateComponent,
  ],
})
export class ManageSmsIntegrationComponent {
  organisation$ = new ReplaySubject<WithRef<IOrganisation>>(1);
  smsProviderMap = SMS_INTEGRATION_COUNTRY_MAP;
  smsProviders$: Observable<SmsIntegrationOption[]>;
  orgUid$: Observable<string>;
  orgSlug$: Observable<string>;
  region$: Observable<Region>;
  savedProvider$: Observable<SmsIntegrationOption | undefined>;
  selectedProvider$: Observable<SmsIntegrationOption | undefined>;
  providerIsUnsaved$: Observable<boolean>;
  smsProviderCtrl = new TypedFormControl<SmsIntegrationOption | undefined>(
    undefined
  );

  integrationsCol = input.required<CollectionReference<IIntegration>>();
  temporaryTokensCol = input.required<CollectionReference<ITemporaryToken>>();

  @Input({ required: true })
  set organisation(value: WithRef<IOrganisation> | undefined) {
    if (value) {
      this.organisation$.next(value);
    }
  }

  constructor(
    private readonly _dialog: BasicDialogService,
    private readonly _snackBar: MatSnackBar,
    private readonly _featureFlags: FeatureFlagsService
  ) {
    const viaApiEnabled$ = this._featureFlags.isFeatureEnabled$(
      VIA_API_SMS_FEATURE
    );

    this.smsProviders$ = combineLatest([
      this.organisation$.pipe(
        map((organisation) => organisation.region),
        map((region) => this.smsProviderMap[region] ?? [])
      ),
      viaApiEnabled$,
    ]).pipe(
      map(([providers, viaApiEnabled]) =>
        viaApiEnabled
          ? [...providers, VIA_API_SMS_INTEGRATION_OPTION]
          : providers
      )
    );

    this.orgUid$ = this.organisation$.pipe(
      map((organisation) => organisation.ref.id)
    );

    this.orgSlug$ = this.organisation$.pipe(
      map((organisation) => organisation.slug)
    );

    this.region$ = this.organisation$.pipe(
      map((organisation) => organisation.region)
    );

    this.savedProvider$ = this.organisation$.pipe(
      map((organisation) => organisation.integrations?.smsProvider)
    );

    this.savedProvider$
      .pipe(take(1), takeUntilDestroyed())
      .subscribe((provider) => {
        if (provider) {
          this.smsProviderCtrl.setValue(provider, { emitEvent: false });
          this.smsProviderCtrl.updateValueAndValidity();
        }
      });

    this.selectedProvider$ = combineLatest([
      formControlChanges$(this.smsProviderCtrl),
      this.savedProvider$,
    ]).pipe(map(([formValue, savedProvider]) => formValue ?? savedProvider));

    this.providerIsUnsaved$ = combineLatest([
      this.savedProvider$,
      this.selectedProvider$,
    ]).pipe(map(([saved, selected]) => saved !== selected));
  }

  getProviderName$(integration: IntegrationType): Observable<string> {
    return of(integration).pipe(
      map((provider) => {
        if (provider === IntegrationType.TNZ) {
          return 'TNZ';
        }

        if (provider === IntegrationType.ViaApi) {
          return 'Via API';
        }

        return new SplitCamelPipe().transform(titlecase(provider));
      })
    );
  }

  async saveSMSPreference(): Promise<void> {
    if (!this.smsProviderCtrl.valid) {
      return;
    }

    const confirmed = await this._dialog.confirm({
      prompt:
        'Are you sure you want to change SMS providers? Please ensure the new SMS provider is fully configured before changing. Any SMS messages sent before the new provider is configured will not be sent.',
      title: 'Change SMS Provider',
      submitLabel: 'Yes, Change Provider',
      submitColor: 'warn',
      cancelLabel: 'Cancel',
    });

    if (!confirmed) {
      return;
    }

    const smsProvider = this.smsProviderCtrl.value;
    const organisation = await snapshot(this.organisation$);

    await Firestore.patchDoc(organisation.ref, {
      integrations: {
        ...organisation.integrations,
        smsProvider,
      },
    });

    this.smsProviderCtrl.markAsPristine();
    this.smsProviderCtrl.updateValueAndValidity();
    this._snackBar.open('SMS provider changed successfully');
  }

  async promptForProviderChange(provider: SmsIntegrationOption): Promise<void> {
    const providerName = titlecase(new SplitCamelPipe().transform(provider));
    const data = confirmationDialogData({
      title: 'Change SMS Provider',
      prompt: `Would you like to change the SMS Provider for this workspace to ${providerName}? Please ensure the new SMS provider is fully configured before changing. Any SMS messages sent before the new provider is configured will not be sent.`,
      submitLabel: 'Yes, Save And Change Provider',
      cancelLabel: 'No, Save Without Changing Provider',
    });
    const confirmed = await this._dialog
      .open<ConfirmDialogComponent, IConfirmationDialogInput, boolean>(
        ConfirmDialogComponent,
        DialogPresets.medium({ data })
      )
      .afterClosed()
      .toPromise();

    if (confirmed) {
      const organisation = await snapshot(this.organisation$);
      await Firestore.patchDoc(organisation.ref, {
        integrations: {
          ...organisation.integrations,
          smsProvider: provider,
        },
      });
    }
  }
}
